import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";
import BangerLogo from "assets/icons/banger/logo-no-icon.svg?react";
import ArrowBackIcon from "assets/icons/glory/arrow-back.svg?react";
import GloryLogo from "assets/icons/glory/glory_logo_mini.svg?react";
import Lottie from "lottie-react";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import useTranslate from "hooks/useTranslate";
import { useRouterHistoryStore } from "stores/routerHistory";
import { useGetJWTSportsBookData } from "queries/useGetJWTSportsBook";
import BangerPreloader from "assets/json/banger_game_preloader.json";
import GloryPreloader from "assets/json/glory_game_preloader.json";
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";
import * as authSelectors from "redux/auth/selectors";
import { usePostMessageGR8 } from "hooks/usePostMessageGR8";
import "./styles.sass";
import { STORAGE_LANGUAGE_NAME } from "../../../../constants/variables";
import { getEnv } from "../../../../utils/hostname";

const IFRAME_PREFIX = "sportsbook";
const prepareIframePath = (pathname: string): string => {
  const cleaned = pathname.replace(
    new RegExp(`(/${IFRAME_PREFIX})+`, "gi"),
    "",
  );
  const normalized = cleaned.replace(/\/{2,}/g, "/");
  return normalized === "" || normalized === "/" ? "/" : normalized;
};
export const SportsBookGamePageMobile = () => {
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const location = useLocation();
  const { VITE_APP_PROJECT_NAME } = import.meta.env;
  const { data, isLoading, refetch } = useGetJWTSportsBookData();
  const [initSrcRef, setInitSrcRef] = useState("");
  const isStage =
    getEnv() === "stage" || getEnv() === "dev" || getEnv() === "local";
  const sportsBookHost = isStage
    ? "https://sport-iframe.pjdoi.xyz"
    : "https://sport-iframe.apcje.xyz";
  const navigate = useNavigate();

  const preloader =
    VITE_APP_PROJECT_NAME === "banger" ? BangerPreloader : GloryPreloader;
  const userLanguage = localStorage.getItem(STORAGE_LANGUAGE_NAME) || "en";

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const prevIframePathRef = useRef<string | null>(null);

  const iframePath = useMemo(() => {
    return prepareIframePath(location.pathname);
  }, [location.pathname]);

  const t = useTranslate();
  const previousPage = useRouterHistoryStore((state) => state.previous);

  useEffect(() => {
    const base = `${sportsBookHost}/${userLanguage === "kz" ? "kk" : userLanguage}${
      iframePath.startsWith("/") ? "" : "/"
    }${iframePath}`;
    if (data?.token && isAuthenticated) {
      setInitSrcRef(`${base}?jwt=${encodeURIComponent(data.token)}`);
    } else {
      setInitSrcRef(base);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.token, userLanguage, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !data?.token) {
      refetch();
    }

    if (!iframePath || prevIframePathRef.current === iframePath) {
      return;
    }

    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage(
        { type: "iframe-navigation", value: { path: iframePath } },
        "*",
      );
      prevIframePathRef.current = iframePath;
    }
  }, [data?.token, iframePath, isAuthenticated, refetch]);

  const handleGoBack = () => {
    navigate(previousPage);
  };

  const handleDeposit = () => {
    navigate({
      search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.deposit })}`,
    });
  };
  const logo =
    VITE_APP_PROJECT_NAME === "banger" ? <BangerLogo /> : <GloryLogo />;

  const pageClass = classNames("SportsBookGamePageMobile", {
    banger: VITE_APP_PROJECT_NAME === "banger",
  });
  usePostMessageGR8();

  return (
    <div className={pageClass}>
      {isLoading ? (
        <div className="SportsBookGamePageMobile__loader">
          <Lottie animationData={preloader} loop={false} />
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={initSrcRef}
          allowFullScreen
          title="Sports Book"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      )}

      <div className="SportsBookGamePageMobile__panel">
        <div className="SportsBookGamePageMobile__panel_left">
          <button
            type="button"
            className="SportsBookGamePageMobile__back"
            onClick={handleGoBack}
          >
            <ArrowBackIcon className="SportsBookGamePageMobile__back_icon" />
          </button>
          <div
            className="SportsBookGamePageMobile__logo"
            onClick={handleGoBack}
          >
            {logo}
          </div>
        </div>
        <div className="SportsBookGamePageMobile__panel_right">
          <div className="SportsBookGamePageMobile-deposit">
            <button
              type="button"
              className="SportsBookGamePageMobile-deposit__btn"
              onClick={handleDeposit}
            >
              {t("profile.balance.deposit.title")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
